import { ABILITIES } from "../content/abilities";
import { CLASSES } from "../content/classes";
import { STATUSES } from "../content/statuses";
import { combat } from "../content/tuning";
import {
  type ClassId,
  type PredId,
  type StatusId,
  type StatusPolarity,
  type TeamId,
  type Unit,
  type World,
} from "./types";
import { ang, dist, dot, fromAngle, norm, sub, vec } from "./vec";

export function classOf(unit: Unit) {
  return CLASSES[unit.classId];
}

export function living(units: Unit[], team?: TeamId): Unit[] {
  return units.filter((u) => isPresent(u) && (team === undefined || u.team === team));
}

export function isPresent(unit: Unit): boolean {
  if (unit.kind === "corpse") return true;
  return !unit.dead;
}

export function isActable(unit: Unit): boolean {
  if (unit.kind === "corpse") return unit.classId === "cultist" && (unit.state.selfCorpse ?? 0) === 1;
  if (unit.dead) return false;
  return true;
}

export function canAct(unit: Unit): boolean {
  return isActable(unit) && unit.lockout <= 0 && !unit.charging;
}

export function canCast(unit: Unit): boolean {
  return canAct(unit) && !hasStatus(unit, "silence") && !unit.casting;
}

export function foesOf(world: World, unit: Unit): Unit[] {
  return living(world.units).filter((u) => u.team !== unit.team);
}

export function alliesOf(world: World, unit: Unit, includeSelf = true): Unit[] {
  return living(world.units).filter((u) => u.team === unit.team && (includeSelf || u.id !== unit.id));
}

export function nearby(world: World, unit: Unit, radius: number, includeSelf = false): Unit[] {
  return living(world.units).filter((u) => {
    if (!includeSelf && u.id === unit.id) return false;
    return dist(unit.pos, u.pos) <= radius + u.radius * 0.25;
  });
}

export function hasStatus(unit: Unit, id: StatusId): boolean {
  return unit.statuses.some((s) => s.id === id);
}

export function statusRemaining(unit: Unit, id: StatusId): number {
  return unit.statuses.find((s) => s.id === id)?.remaining ?? 0;
}

export function enchantmentsOn(unit: Unit) {
  return unit.statuses.filter((s) => STATUSES[s.id].polarity === "enchantment");
}

export function conditionsOn(unit: Unit) {
  return unit.statuses.filter((s) => STATUSES[s.id].polarity === "condition");
}

export function hasPolarity(unit: Unit, polarity: StatusPolarity): boolean {
  return unit.statuses.some((s) => STATUSES[s.id].polarity === polarity);
}

export function isImmobile(unit: Unit): boolean {
  if (hasStatus(unit, "stun") || hasStatus(unit, "stagger") || unit.lockout > 0) return true;
  return unit.stillFor >= 0.12 && !unit.charging;
}

export function isMoving(unit: Unit): boolean {
  return unit.movedThisTick || unit.stillFor < 0.12;
}

export function facingDir(unit: Unit) {
  return fromAngle(unit.facing);
}

export function isFrontal(viewer: Unit, other: Unit): boolean {
  const to = norm(sub(other.pos, viewer.pos));
  if (to.x === 0 && to.y === 0) return true;
  return dot(facingDir(viewer), to) >= combat.frontDot;
}

export function isFacing(viewer: Unit, other: Unit): boolean {
  return isFrontal(viewer, other);
}

export function isFleeing(target: Unit, from: Unit): boolean {
  if (!target.movedThisTick) return false;
  const away = norm(sub(target.pos, from.pos));
  const vel = norm(sub(target.pos, target.prevPos));
  if (vel.x === 0 && vel.y === 0) return false;
  return dot(away, vel) > 0.25;
}

export function inArc(origin: Unit, target: Unit, range: number, arc: number): boolean {
  const d = dist(origin.pos, target.pos);
  if (d > range + target.radius * 0.25) return false;
  if (d <= origin.radius + target.radius + 0.05) return true;
  const to = norm(sub(target.pos, origin.pos));
  const half = arc / 2;
  const delta = Math.acos(Math.max(-1, Math.min(1, dot(facingDir(origin), to))));
  return delta <= half;
}

export function canSee(world: World, viewer: Unit, target: Unit): boolean {
  if (viewer.id === target.id) return true;
  if (!isPresent(viewer) || !isPresent(target)) return false;
  if (hasStatus(viewer, "blind")) return false;
  if (hasStatus(target, "invisible")) return false;
  if (target.classId === "assassin" && target.kind !== "corpse" && !isFacing(viewer, target)) return false;
  void world;
  return true;
}

export function unseenFor(world: World, viewer: Unit, target: Unit): number {
  const last = viewer.seenAt[target.id];
  if (last === undefined) return 999;
  return world.time - last;
}

export function ambushReady(world: World, attacker: Unit, target: Unit): boolean {
  if ((target.ambushGrace[attacker.id] ?? 0) > 0) return true;
  return unseenFor(world, target, attacker) >= 1;
}

export function shieldTotal(unit: Unit, kind?: "any" | "psychic" | "all"): number {
  return unit.shields.reduce((sum, s) => {
    if (kind === "psychic") return s.kind === "psychic" ? sum + s.amount : sum;
    if (kind === "any") return s.kind === "any" ? sum + s.amount : sum;
    return sum + s.amount;
  }, 0);
}

export function hasFaithShield(unit: Unit): boolean {
  return unit.shields.some((s) => s.tag === "faith" && s.amount > 0) || hasStatus(unit, "faith");
}

export function nearSpirit(world: World, unit: Unit, radius = 3.4): boolean {
  return world.units.some(
    (u) => u.kind === "spirit" && !u.dead && dist(u.pos, unit.pos) <= radius + u.radius,
  );
}

export function nearbyEnchantCount(world: World, unit: Unit, radius = 4.2): number {
  let n = 0;
  for (const u of nearby(world, unit, radius, true)) n += enchantmentsOn(u).length;
  return n;
}

export function moveSpeed(unit: Unit): number {
  if (!isActable(unit)) return 0;
  if (hasStatus(unit, "stun") || unit.lockout > 0.01 && hasStatus(unit, "stagger")) {
    if (hasStatus(unit, "stun")) return 0;
  }
  if (hasStatus(unit, "stun")) return 0;
  let s = unit.baseSpeed;
  if (hasStatus(unit, "cripple")) s *= combat.crippleMoveMult;
  if (hasStatus(unit, "fleet") || hasStatus(unit, "tide")) s *= combat.fleetMoveMult;
  s *= windupMoveMult(unit);
  if (unit.kind === "corpse") s *= 0.85;
  return s;
}

export function windupMoveMult(unit: Unit): number {
  const id = unit.casting?.abilityId ?? (unit.charging && unit.charging.remaining > 0 ? unit.charging.abilityId : "");
  if (!id) return 1;
  const def = ABILITIES[id];
  if (!def) return 1;
  return def.windupMove;
}

export function attackCdMult(unit: Unit): number {
  let m = 1;
  if (hasStatus(unit, "frenzy") || hasStatus(unit, "tide")) m *= combat.frenzyCdMult;
  return m;
}

export function evalPred(pred: PredId, world: World, target: Unit, source: Unit | null): boolean {
  void world;
  switch (pred) {
    case "always":
      return true;
    case "immobile":
      return isImmobile(target);
    case "fleeing":
      return !!source && isFleeing(target, source);
    case "hasBleed":
      return hasStatus(target, "bleeding");
    case "hasCripple":
      return hasStatus(target, "cripple");
    case "hasBurning":
      return hasStatus(target, "burning");
    case "hasRegen":
      return hasStatus(target, "regen");
    case "hasIre":
      return hasStatus(target, "ire");
    case "hasRetribution":
      return hasStatus(target, "retribution");
    case "isEnchanted":
      return enchantmentsOn(target).length > 0;
    case "notEnchanted":
      return enchantmentsOn(target).length === 0;
    case "isCorpse":
      return target.kind === "corpse";
    case "lowHp":
      return target.hp <= 3;
    case "facingSource":
      return !!source && isFacing(target, source);
    case "notFacingSource":
      return !!source && !isFacing(target, source);
    case "moving":
      return isMoving(target);
    default:
      return false;
  }
}

export function faceToward(unit: Unit, x: number, y: number): void {
  const dx = x - unit.pos.x;
  const dy = y - unit.pos.y;
  if (dx * dx + dy * dy > 1e-8) unit.facing = ang(vec(dx, dy));
}

export function pickClosest(
  units: Unit[],
  x: number,
  y: number,
  range: number,
  from?: Unit,
): Unit | null {
  let best: Unit | null = null;
  let bestD = Infinity;
  const origin = from ? from.pos : vec(x, y);
  for (const u of units) {
    const d = dist(origin, u.pos);
    if (from && d > range + u.radius * 0.3) continue;
    if (!from && d > range) continue;
    const aimD = dist(u.pos, vec(x, y));
    const score = aimD * 1.2 + d * 0.35;
    if (score < bestD) {
      best = u;
      bestD = score;
    }
  }
  return best;
}

export function heroClasses(): ClassId[] {
  return (Object.keys(CLASSES) as ClassId[]).filter((id) => CLASSES[id].playable);
}
